import React from 'react';
import {Table} from "react-bootstrap";
import css from '../css/PdfHoursExport.module.css'
import {findCorrelatingEvent, floatToDurationString, formatDay} from "./pdfUtils";
import {durationBetween, formatISODate, getAllDaysBetween} from "../../../utils/DateTimeUtills";
import {useTranslation} from "react-i18next";
import useMyTranslation from "../../../Hooks/useMyTranslation";
import ConditionalRender, {Fallback} from "../../ConditionalRender";
import {IoWarning} from "react-icons/io5";


const DaysTablePdf = ({transactions, events, allocations, overlaps, selectedWeek}) => {
    const {i18n} = useTranslation();
    const t = useMyTranslation('export.pdf.dayTable');
    const tTime = useMyTranslation('global.time');
    const tPdfComm = useMyTranslation('export.pdf.common');

    function groupByDay(transactions, timelineEvents) {
        const output = {};

        function sortTransactionsAndEvents(data) {
            for (const [date, transactions] of Object.entries(data)) {
                transactions.sort((a, b) => {
                    const dateA = new Date(a.startTime || a.startDate || a.eventTime);
                    const dateB = new Date(b.startTime || b.startDate || b.eventTime);
                    return dateA - dateB;
                });
            }
        }

        // Iterate over each transaction in the input array
        transactions.forEach(transaction => {
            const { startTime, ...rest } = transaction;
            const date = startTime.split('T')[0];

            if (!output[date]) {
                output[date] = [];
            }

            output[date].push({
                ...rest,
                startTime,
                type: "transaction"
            });
        });

        let correlatedEvents = [];

        timelineEvents.forEach(timelineEvent => {
            // Skip events that were already correlated (merged)
            if (correlatedEvents.includes(timelineEvent.correlationId)) return;

            const correlatingEvent = findCorrelatingEvent(timelineEvent, events);
            correlatingEvent && correlatedEvents.push(correlatingEvent.correlationId);

            const date = timelineEvent.eventTime.split('T')[0];

            if (!output[date]) {
                output[date] = [];
            }

            output[date].push({
                type: "event",
                startTime: timelineEvent.eventTime,
                ...timelineEvent,
                ...(correlatingEvent && { endTime: correlatingEvent.eventTime })
            });
        });

        sortTransactionsAndEvents(output);
        return output;
    }

    const DayTable = ({day, data}) => {
        const totalTimePerCategory = (category) => {
            let total = 0;
            const eventsForDay = data && data.filter(item => item.type === 'event');

            eventsForDay && eventsForDay.forEach(event => {
                if(event.categoryType.toLowerCase() === category.toLowerCase() && event.endTime)
                {
                    total += durationBetween(event.startTime, event.endTime);
                }
            })

            return total;
        }

        const totalNonOverlapping = () => {
            let total = 0;
            const transactionsForDay = data && data.filter(item => item.type === 'transaction');

            const calculatedOverlaps = [];

            transactionsForDay && transactionsForDay.forEach(transaction => {
                // Skip transactions that were already calculated as overlapping.
                if(calculatedOverlaps.includes(transaction.id))
                    return;

                const overlappingTransactions = overlaps.find(overlap => overlap.overlappingTransactions.some(t => t.id === transaction.id));

                // Transaction is overlapping with other transactions. Calculate the total duration of the overlapping transactions.
                if(overlappingTransactions)
                {
                    total += overlappingTransactions.duration;

                    // Add the overlapping transactions to the calculatedOverlaps array to prevent double counting.
                    overlappingTransactions.overlappingTransactions.forEach(t => {
                        if(!calculatedOverlaps.includes(t.id))
                        {
                            calculatedOverlaps.push(t.id);
                        }
                    });
                }
                else // Transaction is not overlapping with other transactions. Calculate the duration of the transaction itself.
                {
                    total += transaction.durationHours;
                }
            })

            return total;
        }

        const overlapsForDay = () => {
            const transactionsForDay = data && data.filter(item => item.type === 'transaction');
            return transactionsForDay && transactionsForDay.filter(transaction => overlaps.find(overlap => overlap.overlappingTransactions.some(t => t.id === transaction.id)));
        }

        const totalForDay = totalNonOverlapping() + totalTimePerCategory('travel') + totalTimePerCategory('custom') + totalTimePerCategory('workLocation') - totalTimePerCategory('break') - totalTimePerCategory('private');

        return (
            <>
                <Table bordered responsive className={css.scalable}>
                    <thead>
                    <tr>
                        <td className={css.tableHeader}>
                            <div className={'d-flex flex-column'}>
                            <span className={'text-capitalize fw-bold'}>
                                {day.toLocaleDateString(i18n.language, {weekday: 'long'})}
                            </span>
                                <small className={'mt-1 fw-medium'}>{formatISODate(day, {
                                    format: 'longDate',
                                    useLocalTime: true
                                })}</small>
                            </div>
                        </td>
                        <td className={`${css.tableHeader} ${css.midX}`}>{tTime('start')}</td>
                        <td className={`${css.tableHeader} ${css.midX}`}>{tTime('end')}</td>
                        <td className={`${css.tableHeader} ${css.midX}`}>{tTime('duration')}</td>
                        <td className={css.tableHeader}>{tPdfComm('comments')}</td>
                    </tr>
                    </thead>
                    <tbody>
                    <ConditionalRender condition={data}>
                        <Fallback>
                            <tr>
                                <td colSpan={5} className={`${css.dayTableRowEmphasis}`}>{tPdfComm('noData')}</td>
                            </tr>
                        </Fallback>

                        {data && data.map((item, index) => {
                            const TransactionRow = ({transaction}) => {
                                const overlappingTransactions = overlaps.find(overlap => overlap.overlappingTransactions.some(t => t.id === transaction.id));
                                const allocationForTransaction = allocations.filter(allocation => allocation.transactionId === transaction.id);

                                return (
                                    <>
                                        <tr>
                                            <td className={`fw-bold ${css.dayTableRowEmphasis}`}>{item.geofenceName}</td>
                                            <td className={css.dayTableRowEmphasis + ' ' + css.topMid}>{formatISODate(transaction.startTime, {
                                                format: 'timeOnly',
                                                useLocalTime: true
                                            })}</td>
                                            <td className={css.dayTableRowEmphasis + ' ' + css.topMid}>{transaction.endTime ? formatISODate(transaction.endTime, {
                                                format: 'timeOnly',
                                                useLocalTime: true
                                            }) : '-'}</td>
                                            <td className={css.dayTableRowEmphasis + ' ' + css.topMid}>
                                                <div className={'d-flex align-items-center justify-content-center'}>
                                                    {transaction.durationHours ? floatToDurationString(transaction.durationHours) : '-'}
                                                    <ConditionalRender condition={overlappingTransactions}>
                                                        <IoWarning size={20} className={'text-warning ms-1'}/>
                                                    </ConditionalRender>
                                                </div>
                                            </td>
                                            <td className={css.dayTableRowEmphasis}>{transaction.note}</td>
                                        </tr>

                                        {allocationForTransaction && allocationForTransaction.length > 0 && allocationForTransaction.map(allocation => (
                                            <React.Fragment key={allocation.transactionId}>
                                                {allocation.workOrdersHourAllocation.map((woAllocation, index) => (
                                                    <tr key={index}>
                                                        <td className={'text-end'}>
                                                            {woAllocation.workOrderNumber}
                                                        </td>
                                                        <td colSpan={2}/>
                                                        <td className={`text-center`}>{floatToDurationString(woAllocation.allocatedHours)}</td>
                                                        <td>{woAllocation.note}</td>
                                                    </tr>
                                                ))}
                                                <ConditionalRender condition={allocation.unallocatedHours > 0}>
                                                    <tr>
                                                        <td className={'text-end fw-bold'}>Unreported</td>
                                                        <td colSpan={2}/>
                                                        <td className={`text-center`}>{floatToDurationString(allocation.unallocatedHours)}</td>
                                                    </tr>
                                                </ConditionalRender>
                                            </React.Fragment>
                                        ))}
                                    </>
                                );
                            }

                            const EventRow = ({event}) => {
                                // let relatedEvent = findCorrelatingEvent(event, events);
                                return (
                                    <tr>
                                        <td className={`fw-bold ${css.dayTableRowEmphasis}`}>{event.name}</td>
                                        <td className={css.dayTableRowEmphasis + ' ' + css.topMid}>{formatISODate(event.startTime, {
                                            format: 'timeOnly',
                                            useLocalTime: true
                                        })}</td>
                                        <td className={css.dayTableRowEmphasis + ' ' + css.topMid}>
                                            <ConditionalRender condition={event.endTime}>
                                                <Fallback>-</Fallback>
                                                {formatISODate(event.endTime, {
                                                    format: 'timeOnly',
                                                    useLocalTime: true,
                                                    backupValue: '-'
                                                })}
                                            </ConditionalRender>
                                        </td>
                                        <td className={css.dayTableRowEmphasis + ' ' + css.topMid}>
                                            <ConditionalRender condition={event.endTime}>
                                                <Fallback>-</Fallback>
                                                {event.endTime && floatToDurationString(durationBetween(event.startTime, event.endTime))}
                                            </ConditionalRender>
                                        </td>
                                        <td  className={css.dayTableRowEmphasis}>{event.note}</td>
                                    </tr>
                                );
                            }

                            return item.type === 'transaction' ? <TransactionRow key={index} transaction={item}/> : <EventRow key={index} event={item}/>;
                        })}

                        <tr>
                            <td colSpan={3} className={css.tableHeader}>{tPdfComm('total')}</td>
                            <td className={`text-center ${css.tableHeader}`}>{floatToDurationString(totalForDay)}</td>
                            <td className={css.tableHeader}/>
                        </tr>
                    </ConditionalRender>
                    </tbody>
                </Table>
                <ConditionalRender condition={data && overlapsForDay().length > 0}>
                    <div className={'mt-3 d-flex align-items-center'}>
                        <IoWarning size={20} className={'text-warning'}/>
                        <span className={'ms-1'}>{t('overlapWarning')}</span>
                    </div>
                </ConditionalRender>
            </>
        );
    }

    const groupedByDay = groupByDay(transactions, events);

    return getAllDaysBetween(selectedWeek.startDate, selectedWeek.endDate).map((day, index) => {
        const workday = groupedByDay[formatDay(day)];

        return (
            <div key={index} className={'my-5'}>
                <DayTable day={day} data={workday}/>
            </div>
        );

    });
};

export default DaysTablePdf;